<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import TemplateUsageReportsAPI from 'dashboard/api/templateUsageReports';

// Eltafouk-only Arabic-first effectiveness dashboard. All visible copy lives in
// this L object so the @intlify lint rule treats them as script data rather
// than bare template strings. Ships Arabic only; no en.json round-trip.
const L = {
  TITLE: 'تقرير استخدام القوالب',
  SUBTITLE:
    'حجم الإرسال، التسليم والقراءة، تفاعل العملاء، وأداء كل قالب وموظف.',
  TZ_NOTE: 'بتوقيت القاهرة',
  EXPORT: 'تصدير Excel',
  FROM: 'من',
  TO: 'إلى',
  APPLY: 'تطبيق',
  CLEAR_FILTERS: 'مسح الفلاتر',
  LOADING: 'جاري التحميل…',
  EMPTY: 'مفيش قوالب اتبعتت في الفترة المختارة.',
  OF: 'من',
  // Row 1
  KPI_SENT: 'رسائل القوالب المرسلة',
  KPI_CUSTOMERS: 'العملاء المستلمون',
  KPI_DELIVERY: 'معدل التسليم',
  KPI_READ: 'معدل القراءة',
  KPI_REPLY: 'معدل رد العملاء',
  KPI_FAIL: 'معدل الفشل',
  AVG_REPLY: 'متوسط أول رد',
  // Row 2
  KPI_USED: 'القوالب المستخدمة',
  KPI_TOP: 'أكثر قالب استخدامًا',
  KPI_BEST: 'أفضل قالب من حيث الرد',
  KPI_ATTENTION: 'قالب يحتاج انتباه',
  USED_NOTE: 'قالب مستخدم',
  SHARE_NOTE: 'من الاستخدام',
  TIMES: 'رسالة',
  REPLY_SHORT: 'رد',
  READ_SHORT: 'قراءة',
  CUSTOMERS_REPLIED: 'عميل ردّوا',
  NONE: 'لا يوجد',
  ST_FAILED: 'فشل',
  // Sort
  SORT_TOTAL: 'الأكثر إرسالًا',
  SORT_READ: 'أعلى قراءة',
  SORT_REPLY: 'أعلى رد',
  SORT_FAIL: 'أعلى فشل',
  // Tables
  TABLE_TITLE: 'مقارنة أداء القوالب',
  TABLE_HINT: 'مرتّبة بالأكثر إرسالًا.',
  AGENT_TITLE: 'استخدام القوالب حسب الموظفين',
  AGENT_HINT: 'مرتّبين بالإجمالي.',
  FAIL_TITLE: 'أسباب فشل الرسائل',
  FAIL_HINT: 'توزيع الرسائل الفاشلة حسب السبب.',
  COL_RANK: '#',
  COL_TEMPLATE: 'القالب',
  COL_AGENT: 'الموظف',
  COL_TOTAL: 'الإرسال',
  COL_CUSTOMERS: 'عملاء',
  COL_DELIVERY: 'تسليم',
  COL_READ: 'قراءة',
  COL_REPLY: 'رد',
  COL_FAIL: 'فشل',
  // Detailed log
  DETAIL_TITLE: 'سجل إرسال القوالب',
  DETAIL_HINT: 'كل عملية إرسال على حدة.',
  SEARCH_PH: 'بحث بالقالب أو الموظف أو العميل أو الرقم…',
  EXPORT_LOG: 'تصدير السجل',
  COL_DATE: 'التاريخ والوقت',
  COL_CUSTOMER: 'العميل',
  COL_NUMBER: 'الرقم',
  COL_STATUS: 'الحالة',
  COL_REPLIED: 'ردّ العميل',
  COL_REASON: 'سبب الفشل',
  OPEN_CONV: 'فتح المحادثة',
  YES: 'نعم',
  NO: 'لا',
  NO_RESULTS: 'مفيش نتائج مطابقة.',
  PREV: 'السابق',
  NEXT: 'التالي',
  PAGE: 'صفحة',
  OF2: 'من',
};

const PRESETS = [
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: 'week', days: 7, label: 'آخر 7 أيام' },
  { key: 'month', days: 30, label: 'آخر 30 يوم' },
  { key: 'quarter', days: 90, label: 'آخر 90 يوم' },
];

const AVATAR_TONES = [
  'bg-n-teal-2 text-n-teal-11',
  'bg-n-blue-2 text-n-blue-11',
  'bg-n-amber-2 text-n-amber-11',
  'bg-n-ruby-2 text-n-ruby-11',
  'bg-n-brand/15 text-n-brand',
];
const TONE = {
  brand: 'bg-n-brand/15 text-n-brand',
  teal: 'bg-n-teal-2 text-n-teal-11',
  blue: 'bg-n-blue-2 text-n-blue-11',
  iris: 'bg-n-iris-2 text-n-iris-11',
  ruby: 'bg-n-ruby-2 text-n-ruby-11',
  slate: 'bg-n-alpha-2 text-n-slate-11',
};
// Minimum received volume before a template qualifies for "best reply rate".
const MIN_VOLUME = 5;

const activePreset = ref('quarter');
const isCustom = ref(false);
const customFrom = ref('');
const customTo = ref('');
const templateSort = ref('total');

const SORT_OPTIONS = [
  { key: 'total', label: 'الأكثر إرسالًا' },
  { key: 'readRate', label: 'أعلى قراءة' },
  { key: 'replyRate', label: 'أعلى رد' },
  { key: 'failRate', label: 'أعلى فشل' },
];

const rows = ref([]);
const loading = ref(false);
const error = ref(null);

// Detailed log table state.
const PAGE_SIZE = 25;
const search = ref('');
const detailDir = ref('desc');
const page = ref(1);

const route = useRoute();
const conversationUrl = id =>
  `/app/accounts/${route.params.accountId}/conversations/${id}`;

const isoDate = d => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const rangeIso = computed(() => {
  const now = new Date();
  if (isCustom.value) {
    return {
      since: customFrom.value || isoDate(now),
      until: customTo.value || isoDate(now),
    };
  }
  const preset = PRESETS.find(p => p.key === activePreset.value);
  if (preset?.key === 'today')
    return { since: isoDate(now), until: isoDate(now) };
  if (preset?.key === 'yesterday') {
    const y = new Date(now - 86400000);
    return { since: isoDate(y), until: isoDate(y) };
  }
  return {
    since: isoDate(new Date(now - preset.days * 86400000)),
    until: isoDate(now),
  };
});

const fetchReport = async () => {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await TemplateUsageReportsAPI.fetch(rangeIso.value);
    rows.value = data.rows || [];
  } catch (e) {
    error.value = e.message || 'فشل التحميل';
  } finally {
    loading.value = false;
  }
};

onMounted(fetchReport);
watch(activePreset, () => {
  if (!isCustom.value) fetchReport();
});
const pickPreset = key => {
  activePreset.value = key;
  isCustom.value = false;
};
const applyCustom = () => {
  isCustom.value = true;
  fetchReport();
};

// ---- Client-side aggregation (so filters will apply everywhere) ----
const round1 = n => Math.round(n * 10) / 10;
const rate = (part, whole) => (whole ? round1((100 * part) / whole) : 0);
const distinct = (list, key) =>
  new Set(list.map(r => r[key]).filter(Boolean)).size;

const agg = list => {
  const byStatus = { sent: 0, delivered: 0, read: 0, failed: 0 };
  list.forEach(r => {
    byStatus[r.status] = (byStatus[r.status] || 0) + 1;
  });
  const received = byStatus.delivered + byStatus.read;
  const receivedRows = list.filter(r => r.received);
  const repliedRows = receivedRows.filter(r => r.replied);
  const replyTimes = repliedRows.map(r => r.reply_secs).filter(s => s != null);
  return {
    total: list.length,
    sent: byStatus.sent,
    delivered: byStatus.delivered,
    read: byStatus.read,
    failed: byStatus.failed,
    received,
    replied: repliedRows.length,
    customers: distinct(list, 'phone'),
    receivedCustomers: distinct(receivedRows, 'phone'),
    repliedCustomers: distinct(repliedRows, 'phone'),
    uniqueTemplates: distinct(list, 'name'),
    deliveryRate: rate(received, list.length),
    readRate: rate(byStatus.read, received),
    failRate: rate(byStatus.failed, list.length),
    replyRate: rate(
      distinct(repliedRows, 'phone'),
      distinct(receivedRows, 'phone')
    ),
    avgReplySecs: replyTimes.length
      ? Math.round(replyTimes.reduce((a, b) => a + b, 0) / replyTimes.length)
      : null,
  };
};

// Pass-through (no filter bar for now); aggregation reads this so filters can
// be reintroduced later without touching the rest of the report.
const filteredRows = computed(() => rows.value);

const summary = computed(() => agg(filteredRows.value));

const groupBy = (list, key) => {
  const map = new Map();
  list.forEach(r => {
    const k = r[key] ?? '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });
  return map;
};

const byTemplate = computed(() => {
  const out = [];
  groupBy(filteredRows.value, 'name').forEach((list, name) => {
    out.push({ name, ...agg(list) });
  });
  return out.sort((a, b) => b.total - a.total);
});

const byAgent = computed(() => {
  const out = [];
  groupBy(filteredRows.value, 'agent').forEach((list, name) => {
    out.push({ name, agent_id: list[0]?.agent_id, ...agg(list) });
  });
  return out.sort((a, b) => b.total - a.total);
});

const failureReasons = computed(() => {
  const failed = filteredRows.value.filter(r => r.status === 'failed');
  const out = [];
  groupBy(failed, 'fail_reason').forEach((list, reason) => {
    out.push({ reason, count: list.length });
  });
  return out.sort((a, b) => b.count - a.count);
});

// Highlights for the row-2 cards.
const topTemplate = computed(() => byTemplate.value[0] || null);
const bestReplyTemplate = computed(() => {
  const eligible = byTemplate.value.filter(t => t.received >= MIN_VOLUME);
  if (!eligible.length) return null;
  return [...eligible].sort((a, b) => b.replyRate - a.replyRate)[0];
});
const attentionTemplate = computed(() => {
  const withFails = byTemplate.value.filter(t => t.failed > 0 && t.total >= 2);
  if (!withFails.length) return null;
  return [...withFails].sort((a, b) => b.failRate - a.failRate)[0];
});

const maxTemplate = computed(() =>
  Math.max(1, ...byTemplate.value.map(t => t.total))
);

// Template performance, re-sortable by the chosen metric.
const TEMPLATE_SORTS = {
  total: t => t.total,
  readRate: t => t.readRate,
  replyRate: t => t.replyRate,
  failRate: t => t.failRate,
};
const sortedTemplates = computed(() => {
  const fn = TEMPLATE_SORTS[templateSort.value] || TEMPLATE_SORTS.total;
  return [...byTemplate.value].sort((a, b) => fn(b) - fn(a));
});

// Detailed per-send log: search → sort (by date) → paginate.
const detailRows = computed(() => {
  const q = search.value.trim().toLowerCase();
  let list = filteredRows.value;
  if (q) {
    list = list.filter(r =>
      [r.name, r.agent, r.contact, r.phone].some(v =>
        String(v ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }
  const dir = detailDir.value === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => (a.ts - b.ts) * dir);
});
const pageCount = computed(() =>
  Math.max(1, Math.ceil(detailRows.value.length / PAGE_SIZE))
);
const pagedRows = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE;
  return detailRows.value.slice(start, start + PAGE_SIZE);
});
watch([search, filteredRows], () => {
  page.value = 1;
});
const prevPage = () => {
  if (page.value > 1) page.value -= 1;
};
const nextPage = () => {
  if (page.value < pageCount.value) page.value += 1;
};
const toggleDetailDir = () => {
  detailDir.value = detailDir.value === 'asc' ? 'desc' : 'asc';
};

// ---- formatting ----
const fmtInt = n => (n || 0).toLocaleString('en');
const pct = n => `${n ?? 0}%`;
const fmtSecs = s => {
  if (s == null) return '—';
  if (s < 60) return `${s} ث`;
  if (s < 3600) return `${Math.round(s / 60)} د`;
  return `${(s / 3600).toFixed(1)} س`;
};
const arabicDateOnly = ts =>
  new Date(ts * 1000).toLocaleDateString('ar-EG', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Africa/Cairo',
  });
const timeOnly = ts =>
  new Date(ts * 1000).toLocaleTimeString('ar-EG', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Africa/Cairo',
  });
const STATUS_BADGE = {
  read: { label: 'مقروء', cls: 'bg-n-teal-2 text-n-teal-11' },
  delivered: { label: 'تم التسليم', cls: 'bg-n-blue-2 text-n-blue-11' },
  sent: { label: 'قيد التسليم', cls: 'bg-n-alpha-2 text-n-slate-11' },
  failed: { label: 'فشل', cls: 'bg-n-ruby-2 text-n-ruby-11' },
};
const statusBadge = s => STATUS_BADGE[s] || STATUS_BADGE.sent;
const replyLabel = r =>
  r.replied ? `${L.YES} · ${fmtSecs(r.reply_secs)}` : L.NO;
const initials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('');
const avatarTone = i => AVATAR_TONES[i % AVATAR_TONES.length];
const failRateClass = r =>
  r > 5 ? 'text-n-ruby-11 font-semibold' : 'text-n-slate-10';

// Compact summary lines for the "top template" card (kept out of the template
// so the bullet separators aren't flagged as bare strings).
const topUsageLine = computed(() => {
  const t = topTemplate.value;
  if (!t) return '';
  return `${fmtInt(t.total)} ${L.TIMES} · ${pct(rate(t.total, summary.value.total))} ${L.SHARE_NOTE}`;
});
const topRatesLine = computed(() => {
  const t = topTemplate.value;
  if (!t) return '';
  return `${L.READ_SHORT} ${pct(t.readRate)} · ${L.REPLY_SHORT} ${pct(t.replyRate)}`;
});

const rangeLabel = computed(() => {
  const r = rangeIso.value;
  return r.since === r.until ? r.since : `${r.since} — ${r.until}`;
});
const hasData = computed(() => rows.value.length > 0);

// Row 1 KPI cards.
const row1 = computed(() => {
  const s = summary.value;
  return [
    {
      key: 'sent',
      label: L.KPI_SENT,
      value: fmtInt(s.total),
      icon: 'i-lucide-send',
      tone: 'brand',
    },
    {
      key: 'customers',
      label: L.KPI_CUSTOMERS,
      value: fmtInt(s.customers),
      icon: 'i-lucide-users',
      tone: 'blue',
    },
    {
      key: 'delivery',
      label: L.KPI_DELIVERY,
      value: pct(s.deliveryRate),
      sub: `${fmtInt(s.received)} ${L.OF} ${fmtInt(s.total)}`,
      icon: 'i-lucide-check-check',
      tone: 'blue',
    },
    {
      key: 'read',
      label: L.KPI_READ,
      value: pct(s.readRate),
      sub: `${fmtInt(s.read)} ${L.OF} ${fmtInt(s.received)}`,
      icon: 'i-lucide-book-open-check',
      tone: 'teal',
    },
    {
      key: 'reply',
      label: L.KPI_REPLY,
      value: pct(s.replyRate),
      sub: `${fmtInt(s.repliedCustomers)} ${L.CUSTOMERS_REPLIED} · ${L.AVG_REPLY} ${fmtSecs(s.avgReplySecs)}`,
      icon: 'i-lucide-reply',
      tone: 'iris',
    },
    {
      key: 'fail',
      label: L.KPI_FAIL,
      value: pct(s.failRate),
      sub: `${fmtInt(s.failed)} ${L.ST_FAILED}`,
      icon: 'i-lucide-triangle-alert',
      tone: 'ruby',
    },
  ];
});

// ---- CSV export ----
const csvCell = v => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (suffix, header, data) => {
  const csv = [header, ...data].map(r => r.map(csvCell).join(',')).join('\n');
  // UTF-8 BOM so Excel opens the Arabic columns correctly.
  const blob = new Blob([String.fromCharCode(0xfeff) + csv], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `template-usage-${suffix}-${rangeIso.value.since}_${rangeIso.value.until}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
const exportCsv = () =>
  downloadCsv(
    'templates',
    [
      L.COL_TEMPLATE,
      L.COL_TOTAL,
      L.COL_CUSTOMERS,
      L.COL_DELIVERY,
      L.COL_READ,
      L.COL_REPLY,
      L.COL_FAIL,
    ],
    byTemplate.value.map(t => [
      t.name,
      t.total,
      t.customers,
      `${t.deliveryRate}%`,
      `${t.readRate}%`,
      `${t.replyRate}%`,
      `${t.failRate}%`,
    ])
  );
const exportDetailCsv = () =>
  downloadCsv(
    'log',
    [
      L.COL_DATE,
      L.COL_TEMPLATE,
      L.COL_AGENT,
      L.COL_CUSTOMER,
      L.COL_NUMBER,
      L.COL_STATUS,
      L.COL_REPLIED,
      L.COL_REASON,
    ],
    detailRows.value.map(r => [
      `${arabicDateOnly(r.ts)} ${timeOnly(r.ts)}`,
      r.name,
      r.agent,
      r.contact,
      r.phone,
      statusBadge(r.status).label,
      r.replied ? `${L.YES} (${fmtSecs(r.reply_secs)})` : L.NO,
      r.fail_reason || '',
    ])
  );
</script>

<template>
  <div class="flex flex-col gap-5 p-6">
    <!-- Header -->
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div class="flex items-center gap-3">
        <span
          class="grid size-10 place-items-center rounded-xl bg-n-brand/15 text-n-brand"
        >
          <span class="i-lucide-layout-template size-5" />
        </span>
        <div>
          <h1 class="text-[18px] font-semibold tracking-tight text-n-slate-12">
            {{ L.TITLE }}
          </h1>
          <p class="mt-0.5 text-[12.5px] text-n-slate-11">{{ L.SUBTITLE }}</p>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex shrink-0 items-center gap-2 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-[12.5px] font-medium text-n-slate-12 shadow-sm transition-colors hover:bg-n-alpha-2"
        @click="exportCsv"
      >
        <span class="i-lucide-download size-4 text-n-slate-11" />
        {{ L.EXPORT }}
      </button>
    </header>

    <!-- Filter bar -->
    <div
      class="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-n-weak bg-n-solid-1 p-2.5 shadow-sm"
    >
      <div class="flex items-center gap-1 rounded-xl bg-n-alpha-1 p-1">
        <button
          v-for="p in PRESETS"
          :key="p.key"
          type="button"
          class="rounded-lg px-3 py-1.5 text-[12.5px] font-medium transition-all"
          :class="
            !isCustom && activePreset === p.key
              ? 'bg-n-brand text-white shadow-sm'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          @click="pickPreset(p.key)"
        >
          {{ p.label }}
        </button>
      </div>
      <div class="h-6 w-px bg-n-weak" />
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-[12px] text-n-slate-10">{{ L.FROM }}</span>
        <label
          class="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-colors focus-within:border-n-brand"
          :class="
            isCustom
              ? 'border-n-slate-5 bg-n-solid-1'
              : 'border-n-weak bg-n-alpha-1'
          "
        >
          <span class="i-lucide-calendar size-3.5 text-n-slate-10" />
          <input
            v-model="customFrom"
            type="date"
            dir="ltr"
            class="w-[112px] bg-transparent text-[12.5px] tabular-nums text-n-slate-12 outline-none"
          />
        </label>
        <span class="text-[12px] text-n-slate-10">{{ L.TO }}</span>
        <label
          class="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-colors focus-within:border-n-brand"
          :class="
            isCustom
              ? 'border-n-slate-5 bg-n-solid-1'
              : 'border-n-weak bg-n-alpha-1'
          "
        >
          <span class="i-lucide-calendar size-3.5 text-n-slate-10" />
          <input
            v-model="customTo"
            type="date"
            dir="ltr"
            class="w-[112px] bg-transparent text-[12.5px] tabular-nums text-n-slate-12 outline-none"
          />
        </label>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-[12.5px] font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          @click="applyCustom"
        >
          <span class="i-lucide-check size-3.5" />
          {{ L.APPLY }}
        </button>
      </div>
      <div
        class="ms-auto inline-flex items-center gap-1.5 rounded-lg bg-n-alpha-1 px-2.5 py-1.5 text-[12px] tabular-nums text-n-slate-11"
      >
        <span class="i-lucide-clock-3 size-3.5 text-n-slate-10" />
        <span dir="ltr">{{ rangeLabel }}</span>
        <span class="size-1 rounded-full bg-n-slate-8" />
        <span>{{ L.TZ_NOTE }}</span>
      </div>
    </div>

    <!-- Loading / error / empty -->
    <div
      v-if="loading"
      class="flex items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 py-16 text-n-slate-11"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      {{ L.LOADING }}
    </div>
    <div
      v-else-if="error"
      class="rounded-xl bg-n-ruby-2 px-4 py-3 text-n-ruby-12 ring-1 ring-n-ruby-6"
    >
      {{ error }}
    </div>
    <div
      v-else-if="!hasData"
      class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-16 text-center text-n-slate-11"
    >
      {{ L.EMPTY }}
    </div>

    <template v-else>
      <!-- Row 1: headline KPIs -->
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <div
          v-for="k in row1"
          :key="k.key"
          class="group rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-all hover:border-n-slate-5 hover:shadow-sm"
        >
          <div class="flex items-center justify-between">
            <p
              class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
            >
              {{ k.label }}
            </p>
            <span
              class="grid size-7 place-items-center rounded-lg transition-transform group-hover:scale-110"
              :class="TONE[k.tone]"
            >
              <span :class="`${k.icon} size-4`" />
            </span>
          </div>
          <p
            class="mt-2 text-[24px] font-semibold tabular-nums text-n-slate-12"
            dir="ltr"
          >
            {{ k.value }}
          </p>
          <p v-if="k.sub" class="mt-0.5 truncate text-[11px] text-n-slate-10">
            {{ k.sub }}
          </p>
        </div>
      </div>

      <!-- Row 2: analysis cards -->
      <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <!-- used -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between">
            <p
              class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
            >
              {{ L.KPI_USED }}
            </p>
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <span class="i-lucide-layers size-4" />
            </span>
          </div>
          <p
            class="mt-2 text-[24px] font-semibold tabular-nums text-n-slate-12"
            dir="ltr"
          >
            {{ fmtInt(summary.uniqueTemplates) }}
          </p>
          <p class="mt-0.5 text-[11px] text-n-slate-10">{{ L.USED_NOTE }}</p>
        </div>

        <!-- top used -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between">
            <p
              class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
            >
              {{ L.KPI_TOP }}
            </p>
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-amber-2 text-n-amber-11"
            >
              <span class="i-lucide-crown size-4" />
            </span>
          </div>
          <template v-if="topTemplate">
            <p
              class="mt-2 truncate text-[14px] font-semibold text-n-slate-12"
              dir="ltr"
              :title="topTemplate.name"
            >
              {{ topTemplate.name }}
            </p>
            <p class="mt-1 text-[11px] text-n-slate-10">{{ topUsageLine }}</p>
            <p class="mt-0.5 text-[11px] text-n-slate-11">{{ topRatesLine }}</p>
          </template>
          <p v-else class="mt-2 text-[14px] text-n-slate-10">{{ L.NONE }}</p>
        </div>

        <!-- best reply -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="flex items-center justify-between">
            <p
              class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
            >
              {{ L.KPI_BEST }}
            </p>
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-iris-2 text-n-iris-11"
            >
              <span class="i-lucide-trophy size-4" />
            </span>
          </div>
          <template v-if="bestReplyTemplate">
            <p
              class="mt-2 truncate text-[14px] font-semibold text-n-slate-12"
              dir="ltr"
              :title="bestReplyTemplate.name"
            >
              {{ bestReplyTemplate.name }}
            </p>
            <p
              class="mt-1 text-[18px] font-bold tabular-nums text-n-iris-11"
              dir="ltr"
            >
              {{ pct(bestReplyTemplate.replyRate) }}
            </p>
            <p class="mt-0.5 text-[11px] text-n-slate-10">
              {{ fmtInt(bestReplyTemplate.repliedCustomers) }} {{ L.OF }}
              {{ fmtInt(bestReplyTemplate.receivedCustomers) }}
            </p>
          </template>
          <p v-else class="mt-2 text-[14px] text-n-slate-10">{{ L.NONE }}</p>
        </div>

        <!-- needs attention -->
        <div
          class="rounded-xl border p-4"
          :class="
            attentionTemplate
              ? 'border-n-ruby-6 bg-n-ruby-2/40'
              : 'border-n-weak bg-n-solid-1'
          "
        >
          <div class="flex items-center justify-between">
            <p
              class="text-[11px] font-bold uppercase tracking-wider text-n-slate-10"
            >
              {{ L.KPI_ATTENTION }}
            </p>
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-ruby-2 text-n-ruby-11"
            >
              <span class="i-lucide-triangle-alert size-4" />
            </span>
          </div>
          <template v-if="attentionTemplate">
            <p
              class="mt-2 truncate text-[14px] font-semibold text-n-slate-12"
              dir="ltr"
              :title="attentionTemplate.name"
            >
              {{ attentionTemplate.name }}
            </p>
            <p
              class="mt-1 text-[18px] font-bold tabular-nums text-n-ruby-11"
              dir="ltr"
            >
              {{ pct(attentionTemplate.failRate) }}
            </p>
            <p class="mt-0.5 text-[11px] text-n-slate-10">
              {{ fmtInt(attentionTemplate.failed) }} {{ L.ST_FAILED }}
              {{ L.OF }} {{ fmtInt(attentionTemplate.total) }}
            </p>
          </template>
          <p v-else class="mt-2 text-[14px] text-n-slate-10">{{ L.NONE }}</p>
        </div>
      </div>

      <!-- By agent -->
      <div class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1">
        <div class="border-b border-n-weak px-5 py-3.5">
          <div class="flex items-center gap-2">
            <span class="i-lucide-users size-4 text-n-slate-11" />
            <h2 class="text-[15px] font-semibold text-n-slate-12">
              {{ L.AGENT_TITLE }}
            </h2>
          </div>
          <p class="mt-0.5 text-[12px] text-n-slate-10">{{ L.AGENT_HINT }}</p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[520px] text-[13px]">
            <thead>
              <tr
                class="border-b border-n-weak bg-n-alpha-1 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                <th class="px-4 py-2.5 text-start">{{ L.COL_AGENT }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_TOTAL }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_CUSTOMERS }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_READ }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_REPLY }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_FAIL }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(a, i) in byAgent"
                :key="a.agent_id ?? a.name"
                class="border-b border-n-weak/60 transition-colors last:border-0 hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2.5">
                    <span
                      class="grid size-7 shrink-0 place-items-center rounded-full text-[10.5px] font-bold"
                      :class="avatarTone(i)"
                    >
                      {{ initials(a.name) }}
                    </span>
                    <span class="font-medium text-n-slate-12">
                      {{ a.name }}
                    </span>
                  </div>
                </td>
                <td
                  class="px-2 py-3 text-center font-bold tabular-nums text-n-slate-12"
                >
                  {{ fmtInt(a.total) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-slate-11">
                  {{ fmtInt(a.customers) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-teal-11">
                  {{ pct(a.readRate) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-iris-11">
                  {{ pct(a.replyRate) }}
                </td>
                <td
                  class="px-2 py-3 text-center tabular-nums"
                  :class="failRateClass(a.failRate)"
                >
                  {{ pct(a.failRate) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Template comparison table -->
      <div class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1">
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3.5"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-table size-4 text-n-slate-11" />
            <h2 class="text-[15px] font-semibold text-n-slate-12">
              {{ L.TABLE_TITLE }}
            </h2>
          </div>
          <div class="flex items-center gap-1 rounded-lg bg-n-alpha-1 p-0.5">
            <button
              v-for="s in SORT_OPTIONS"
              :key="s.key"
              type="button"
              class="rounded-md px-2 py-1 text-[11.5px] font-medium transition-all"
              :class="
                templateSort === s.key
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="templateSort = s.key"
            >
              {{ s.label }}
            </button>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[760px] text-[13px]">
            <thead>
              <tr
                class="border-b border-n-weak bg-n-alpha-1 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                <th class="px-3 py-2.5 text-center">{{ L.COL_RANK }}</th>
                <th class="px-4 py-2.5 text-start">{{ L.COL_TEMPLATE }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_TOTAL }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_CUSTOMERS }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_DELIVERY }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_READ }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_REPLY }}</th>
                <th class="px-2 py-2.5 text-center">{{ L.COL_FAIL }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(t, i) in sortedTemplates"
                :key="t.name"
                class="border-b border-n-weak/60 transition-colors last:border-0 hover:bg-n-alpha-1"
                :class="i === 0 ? 'bg-n-brand/[0.03]' : ''"
              >
                <td
                  class="px-3 py-3 text-center text-[12px] font-bold tabular-nums"
                  :class="i === 0 ? 'text-n-brand' : 'text-n-slate-9'"
                >
                  {{ i + 1 }}
                </td>
                <td class="px-4 py-3">
                  <p class="font-semibold text-n-slate-12" dir="ltr">
                    {{ t.name }}
                  </p>
                  <div
                    class="mt-1 h-1 w-32 overflow-hidden rounded-full bg-n-alpha-2"
                  >
                    <div
                      class="h-full rounded-full"
                      :class="i === 0 ? 'bg-n-brand' : 'bg-n-slate-8'"
                      :style="{ width: `${(100 * t.total) / maxTemplate}%` }"
                    />
                  </div>
                </td>
                <td
                  class="px-2 py-3 text-center text-[15px] font-bold tabular-nums text-n-slate-12"
                >
                  {{ fmtInt(t.total) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-slate-11">
                  {{ fmtInt(t.customers) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-blue-11">
                  {{ pct(t.deliveryRate) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-teal-11">
                  {{ pct(t.readRate) }}
                </td>
                <td class="px-2 py-3 text-center tabular-nums text-n-iris-11">
                  {{ pct(t.replyRate) }}
                </td>
                <td
                  class="px-2 py-3 text-center tabular-nums"
                  :class="failRateClass(t.failRate)"
                >
                  {{ pct(t.failRate) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Failure reasons -->
      <div class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1">
        <div class="border-b border-n-weak px-5 py-3.5">
          <div class="flex items-center gap-2">
            <span class="i-lucide-circle-alert size-4 text-n-slate-11" />
            <h2 class="text-[15px] font-semibold text-n-slate-12">
              {{ L.FAIL_TITLE }}
            </h2>
          </div>
          <p class="mt-0.5 text-[12px] text-n-slate-10">{{ L.FAIL_HINT }}</p>
        </div>
        <div
          v-if="!failureReasons.length"
          class="px-5 py-10 text-center text-[13px] text-n-slate-10"
        >
          {{ L.NONE }}
        </div>
        <div v-else class="flex flex-col gap-3 p-5">
          <div v-for="f in failureReasons" :key="f.reason">
            <div class="mb-1 flex items-center justify-between text-[12.5px]">
              <span class="text-n-slate-11">{{ f.reason }}</span>
              <span class="font-semibold tabular-nums text-n-slate-12">
                {{ fmtInt(f.count) }}
              </span>
            </div>
            <div class="h-2 w-full overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-full rounded-full bg-n-ruby-9"
                :style="{
                  width: `${(100 * f.count) / (summary.failed || 1)}%`,
                }"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Detailed log -->
      <div class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1">
        <div
          class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-5 py-3.5"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-list size-4 text-n-slate-11" />
            <h2 class="text-[15px] font-semibold text-n-slate-12">
              {{ L.DETAIL_TITLE }}
            </h2>
            <span class="text-[12px] text-n-slate-10">{{ L.DETAIL_HINT }}</span>
          </div>
          <div class="flex items-center gap-2">
            <label
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-1.5 focus-within:border-n-brand"
            >
              <span class="i-lucide-search size-3.5 text-n-slate-10" />
              <input
                v-model="search"
                type="search"
                :placeholder="L.SEARCH_PH"
                class="w-56 bg-transparent text-[12.5px] text-n-slate-12 outline-none"
              />
            </label>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-[12.5px] font-medium text-n-slate-12 hover:bg-n-alpha-2"
              @click="exportDetailCsv"
            >
              <span class="i-lucide-download size-3.5 text-n-slate-11" />
              {{ L.EXPORT_LOG }}
            </button>
          </div>
        </div>

        <div
          v-if="!detailRows.length"
          class="px-5 py-12 text-center text-[13px] text-n-slate-10"
        >
          {{ L.NO_RESULTS }}
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full min-w-[920px] text-[13px]">
            <thead>
              <tr
                class="border-b border-n-weak bg-n-alpha-1 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
              >
                <th class="px-4 py-2.5 text-start">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1 hover:text-n-slate-12"
                    @click="toggleDetailDir"
                  >
                    {{ L.COL_DATE }}
                    <span
                      class="size-3"
                      :class="
                        detailDir === 'asc'
                          ? 'i-lucide-arrow-up'
                          : 'i-lucide-arrow-down'
                      "
                    />
                  </button>
                </th>
                <th class="px-3 py-2.5 text-start">{{ L.COL_TEMPLATE }}</th>
                <th class="px-3 py-2.5 text-start">{{ L.COL_AGENT }}</th>
                <th class="px-3 py-2.5 text-start">{{ L.COL_CUSTOMER }}</th>
                <th class="px-3 py-2.5 text-start">{{ L.COL_NUMBER }}</th>
                <th class="px-3 py-2.5 text-center">{{ L.COL_STATUS }}</th>
                <th class="px-3 py-2.5 text-center">{{ L.COL_REPLIED }}</th>
                <th class="px-3 py-2.5 text-start">{{ L.COL_REASON }}</th>
                <th class="px-3 py-2.5 text-center" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in pagedRows"
                :key="r.id"
                class="border-b border-n-weak/60 transition-colors last:border-0 hover:bg-n-alpha-1"
              >
                <td class="whitespace-nowrap px-4 py-3">
                  <p class="text-n-slate-12">{{ arabicDateOnly(r.ts) }}</p>
                  <p class="text-[11px] tabular-nums text-n-slate-10" dir="ltr">
                    {{ timeOnly(r.ts) }}
                  </p>
                </td>
                <td class="px-3 py-3 font-medium text-n-slate-12" dir="ltr">
                  {{ r.name }}
                </td>
                <td class="px-3 py-3 text-n-slate-11">{{ r.agent }}</td>
                <td class="px-3 py-3 text-n-slate-11">
                  {{ r.contact || '—' }}
                </td>
                <td class="px-3 py-3 tabular-nums text-n-slate-10" dir="ltr">
                  {{ r.phone || '—' }}
                </td>
                <td class="px-3 py-3 text-center">
                  <span
                    class="inline-block rounded-md px-2 py-0.5 text-[11.5px] font-medium"
                    :class="statusBadge(r.status).cls"
                  >
                    {{ statusBadge(r.status).label }}
                  </span>
                </td>
                <td
                  class="px-3 py-3 text-center tabular-nums"
                  :class="r.replied ? 'text-n-iris-11' : 'text-n-slate-9'"
                >
                  {{ replyLabel(r) }}
                </td>
                <td class="px-3 py-3 text-[12px] text-n-ruby-11">
                  {{ r.fail_reason || '' }}
                </td>
                <td class="px-3 py-3 text-center">
                  <a
                    v-if="r.conversation_id"
                    :href="conversationUrl(r.conversation_id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 rounded-md border border-n-weak px-2 py-1 text-[11.5px] text-n-slate-11 hover:bg-n-alpha-2"
                  >
                    <span class="i-lucide-external-link size-3" />
                    {{ L.OPEN_CONV }}
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="detailRows.length"
          class="flex items-center justify-between border-t border-n-weak px-5 py-3 text-[12.5px] text-n-slate-11"
        >
          <span class="tabular-nums">{{ fmtInt(detailRows.length) }}</span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="rounded-md border border-n-weak px-2.5 py-1 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="page <= 1"
              @click="prevPage"
            >
              {{ L.PREV }}
            </button>
            <span class="tabular-nums" dir="ltr">
              {{ `${page} / ${pageCount}` }}
            </span>
            <button
              type="button"
              class="rounded-md border border-n-weak px-2.5 py-1 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="page >= pageCount"
              @click="nextPage"
            >
              {{ L.NEXT }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
